import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { V, Haptic } from '../utils/theme';
import { LS } from '../utils/storage';
import { Icons } from '../components/Icons';
import { SectionTitle, onAccent } from '../components/kit';
import { useLayout } from '../utils/responsive';
import { Card, Btn, Field, Sheet, Chip, Progress, Stat, ValidationWarning, validateNutrition, SuccessToastCtrl, ConfirmCtrl } from '../components/ui';
import { today, ago, fmtShort, fmtFull, uid, wUnit, chartCfg } from '../utils/helpers';
import { Undo } from '../utils/undo';
import { FOODS, FOOD_CATS } from '../data/foods';
import { MEAL_IDEAS, MEAL_TYPES, MEAL_TAGS } from '../data/meals';
import { FoodSearch, BarcodeScanner, useNutritionStreak } from '../components/dialogs';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { ShareCard } from '../utils/share';
import { checkAndAwardMissions } from './gamification';
import { useNutritionGoals, CoachTargetsNote } from '../components/CoachNutrition';

const EMPTY_MEALS=()=>[{name:"Breakfast",items:[]},{name:"Lunch",items:[]},{name:"Dinner",items:[]},{name:"Snacks",items:[]}];

export function NutritionTab({s,d}){
  const { isDesktop } = useLayout();
  // Coach-set targets (org) replace the member's own goals on screen; s.goals is never written
  const nt=useNutritionGoals(s.goals);
  const goals=nt.goals;
  const [show,setShow]=useState(false);
  const [form,setForm]=useState({date:today(),cal:"",protein:"",carbs:"",fat:"",fiber:"",sugar:"",sodium:"",water:"",sleep:"",
    meals:EMPTY_MEALS()});
  const [activeMeal,setActiveMeal]=useState(null);
  const [scanMeal,setScanMeal]=useState(null);
  const [valWarnings,setValWarnings]=useState(null);
  const [favName,setFavName]=useState("");
  const [showFavInput,setShowFavInput]=useState(false);
  const [editingNId,setEditingNId]=useState(null);
  const [showIdeas,setShowIdeas]=useState(false);     // meal ideas browser
  const [ideaMealTarget,setIdeaMealTarget]=useState(null); // force ideas into a specific meal slot
  const [ideaType,setIdeaType]=useState("All");
  const [ideaTag,setIdeaTag]=useState("All");
  const [quickAdd,setQuickAdd]=useState(false);       // quick-add calories modal
  const [quick,setQuick]=useState({cal:"",protein:""});
  const [expandedN,setExpandedN]=useState(null);      // expanded history entry
  const td=today();
  const [waterCount,setWaterCount]=useState(()=>parseInt(LS.get("ft-water-"+today()))||0);
  // Re-read when the calendar day changes (app left open overnight on the home screen)
  useEffect(()=>{setWaterCount(Math.max(parseInt(LS.get("ft-water-"+td))||0,s.nutrition.find(n=>n.date===td)?.water||0));},[td]);
  // Water taps write to localStorage AND to the day's nutrition entry, so they survive a
  // device change and show up in history/export (previously the counter was LS-only).
  const setWater=(n)=>{
    setWaterCount(n);LS.set("ft-water-"+td,n);
    const ex=s.nutrition.find(x=>x.date===td);
    if(ex&&(ex.water||0)!==n)d({type:"EDIT_N",n:{...ex,water:n}});
  };
  const waterGoal=parseInt(goals?.water)||8;
  // Track stray timers so callbacks don't fire after unmount
  const _nutTimersRef=useRef([]);
  useEffect(()=>()=>{_nutTimersRef.current.forEach(clearTimeout);_nutTimersRef.current=[];},[]);

  // Build a form object from an existing entry (or a fresh one for today)
  const buildForm=(ex)=>ex
    ?{...ex,cal:ex.cal?.toString()||"",protein:ex.protein?.toString()||"",carbs:ex.carbs?.toString()||"",fat:ex.fat?.toString()||"",
      fiber:(ex.fiber||"").toString(),sugar:(ex.sugar||"").toString(),sodium:(ex.sodium||"").toString(),
      water:(ex.water||"").toString(),sleep:(ex.sleep||"").toString(),meals:ex.meals||EMPTY_MEALS()}
    :{date:today(),cal:"",protein:"",carbs:"",fat:"",fiber:"",sugar:"",sodium:"",water:"",sleep:"",meals:EMPTY_MEALS()};

  const start=()=>{
    const ex=s.nutrition.find(n=>n.date===today());
    setForm(buildForm(ex));
    setEditingNId(ex?ex.id:null);
    setShow(true);
  };

  // #12 Copy from yesterday
  const copyYesterday=()=>{
    const prev=s.nutrition.find(n=>n.date===ago(1));
    if(prev){
      setForm({...buildForm(prev),date:today(),sleep:""});
      setEditingNId(s.nutrition.find(n=>n.date===today())?.id||null);
      setShow(true);
    }
  };

  const addFoodToMeal=(mi,food)=>{setForm(f=>{const m=[...f.meals];m[mi]={...m[mi],items:[...(m[mi].items||[]),food]};return{...f,meals:m};});};
  const rmFoodFromMeal=(mi,fi)=>{setForm(f=>{const m=[...f.meals];m[mi]={...m[mi],items:m[mi].items.filter((_,i)=>i!==fi)};return{...f,meals:m};});};

  // Add a curated meal idea's items to a meal slot — opens the log sheet pre-filled
  const addIdea=(idea)=>{
    Haptic.light();
    const slot=ideaMealTarget!==null?ideaMealTarget
      :({Breakfast:0,Lunch:1,Dinner:2,Snack:3}[idea.type]??3);
    const ex=s.nutrition.find(n=>n.date===today());
    // If sheet is already open keep current form; else build from today's entry
    const base=show?form:buildForm(ex);
    if(!show)setEditingNId(ex?ex.id:null);
    const meals=[...(base.meals||EMPTY_MEALS())];
    meals[slot]={...meals[slot],items:[...(meals[slot].items||[]),...idea.items.map(i=>({...i}))]};
    setForm({...base,meals});
    setShowIdeas(false);setIdeaMealTarget(null);setShow(true);
    SuccessToastCtrl.show(`Added: ${idea.n} → ${meals[slot].name}`);
  };

  const itemTotals=(meals)=>{
    let cal=0,protein=0,carbs=0,fat=0,fiber=0,count=0;
    (meals||[]).forEach(m=>(m.items||[]).forEach(i=>{count++;cal+=(i.cal||0);protein+=(i.protein||0);carbs+=(i.carbs||0);fat+=(i.fat||0);fiber+=(i.fiber||0);}));
    return{cal:Math.round(cal),protein:Math.round(protein),carbs:Math.round(carbs),fat:Math.round(fat),fiber:Math.round(fiber),count};
  };
  const autoCalc=()=>{
    const t=itemTotals(form.meals);
    autoTotalsRef.current=[t.cal,t.protein,t.carbs,t.fat].join("|");
    setForm(f=>({...f,cal:t.cal.toString(),protein:t.protein.toString(),carbs:t.carbs.toString(),fat:t.fat.toString(),
      ...(t.fiber>0?{fiber:t.fiber.toString()}:{})}));
  };
  // Totals follow the food items automatically until the user types their own numbers.
  // Previously items only reached the totals via a manual "Auto-calculate" tap, so an
  // itemized day could save as 0 kcal and every ring/chart/streak saw nothing.
  const autoTotalsRef=useRef(null);
  useEffect(()=>{
    if(!show)return;
    const t=itemTotals(form.meals);
    if(t.count===0)return;
    const cur=[form.cal,form.protein,form.carbs,form.fat].map(v=>parseInt(v)||0).join("|");
    const next=[t.cal,t.protein,t.carbs,t.fat].join("|");
    if(cur==="0|0|0|0"||cur===autoTotalsRef.current){
      if(next!==cur)autoCalc();else autoTotalsRef.current=next;
    }
  },[show,form.meals]);

  const save=()=>{
    // Safety net: an itemized day with untouched totals still saves its real numbers
    const t=itemTotals(form.meals);
    const blank=!(parseInt(form.cal)||parseInt(form.protein)||parseInt(form.carbs)||parseInt(form.fat));
    const tot=blank&&t.count>0?t:{cal:parseInt(form.cal)||0,protein:parseInt(form.protein)||0,carbs:parseInt(form.carbs)||0,fat:parseInt(form.fat)||0,fiber:parseInt(form.fiber)||0};
    const n={id:editingNId||uid(),date:form.date,cal:tot.cal,protein:tot.protein,
      carbs:tot.carbs,fat:tot.fat,fiber:parseInt(form.fiber)||tot.fiber||0,
      sugar:parseInt(form.sugar)||0,sodium:parseInt(form.sodium)||0,
      // The tap counter on the tab is the primary water log; the form field only overrides it
      water:parseInt(form.water)||(form.date===td?waterCount:0),sleep:parseFloat(form.sleep)||0,meals:form.meals};
    const warnings=validateNutrition(n);
    if(warnings.length>0&&!valWarnings){setValWarnings(warnings);return;}
    setValWarnings(null);
    // The date was changed to a day that already has a log: add to it rather than replace it
    const clash=s.nutrition.find(x=>x.date===n.date&&x.id!==editingNId);
    if(clash){d({type:"MERGE_N",n});if(editingNId)d({type:"DEL_N",id:editingNId});SuccessToastCtrl.show(`Added to ${n.date===td?"today's":"that day's"} log`);}
    else if(editingNId){d({type:"EDIT_N",n});SuccessToastCtrl.show("Nutrition updated");}
    else{d({type:"ADD_N",n});SuccessToastCtrl.show("Nutrition logged +8 XP 🥗");
      const t=setTimeout(()=>checkAndAwardMissions({...s,nutrition:[n,...s.nutrition]},d),400);
      _nutTimersRef.current.push(t);}
    Haptic.medium();setEditingNId(null);setShow(false);
  };

  // Quick-add: bump today's totals without opening the full form
  const saveQuickAdd=()=>{
    const qc=parseInt(quick.cal)||0,qp=parseInt(quick.protein)||0;
    if(qc<=0&&qp<=0){setQuickAdd(false);return;}
    const ex=s.nutrition.find(n=>n.date===today());
    if(ex){d({type:"EDIT_N",n:{...ex,cal:(ex.cal||0)+qc,protein:(ex.protein||0)+qp}});}
    else{d({type:"ADD_N",n:{id:uid(),date:today(),cal:qc,protein:qp,carbs:0,fat:0,fiber:0,water:0,sleep:0,meals:EMPTY_MEALS()}});}
    Haptic.medium();SuccessToastCtrl.show(`Quick added ${qc>0?`${qc} cal`:""}${qc>0&&qp>0?" · ":""}${qp>0?`${qp}g protein`:""}`);
    setQuick({cal:"",protein:""});setQuickAdd(false);
  };

  // Unsaved changes = the form no longer matches the entry it was opened from (or a blank day)
  const formKey=f=>JSON.stringify([f.date,f.cal,f.protein,f.carbs,f.fat,f.fiber,f.sugar,f.sodium,f.water,f.sleep,
    (f.meals||[]).map(m=>[m.name,m.time||"",(m.items||[]).map(i=>[i.name,i.cal])])]);
  const closeLog=()=>{setShow(false);setEditingNId(null);};
  const requestCloseLog=()=>{
    const base=buildForm(editingNId?s.nutrition.find(n=>n.id===editingNId):null);
    if(formKey(form)===formKey(base))closeLog();
    else ConfirmCtrl.show("Discard unsaved changes?","The food and numbers you added to this log haven't been saved.",closeLog);
  };

  const editEntry=(n)=>{
    setForm(buildForm(n));
    setEditingNId(n.id);setShow(true);
  };

  const week=s.nutrition.filter(n=>n.date>=ago(7));
  const avgCal=week.length?Math.round(week.reduce((s,n)=>s+n.cal,0)/week.length):0;
  const avgP=week.length?Math.round(week.reduce((s,n)=>s+n.protein,0)/week.length):0;
  const protGoalVal=goals?.protein||0;
  const protHitDays=protGoalVal>0?week.filter(n=>n.protein>=protGoalVal*0.9).length:0;
  const protHitRate=week.length&&protGoalVal>0?Math.round(protHitDays/week.length*100):0;
  const totalItems=form.meals.reduce((s,m)=>(m.items||[]).length+s,0);

  const macroChart=useMemo(()=>
    s.nutrition.filter(n=>n.date>=ago(s.range)).sort((a,b)=>a.date.localeCompare(b.date))
      .map(n=>({date:fmtShort(n.date),P:n.protein,C:n.carbs,F:n.fat}))
  ,[s.nutrition,s.range]);

  const todayN=s.nutrition.find(n=>n.date===today());
  const tCal=todayN?.cal||0,tProt=todayN?.protein||0,tCarbs=todayN?.carbs||0,tFat=todayN?.fat||0;
  const calGoal=goals?.cal||2400,protGoal=goals?.protein||180;
  const calPct=Math.min(100,Math.round(tCal/calGoal*100)),protPct=Math.min(100,Math.round(tProt/protGoal*100));
  const nutritionStreak=useNutritionStreak(s.nutrition);

  return(
    <div style={{display:"flex",flexDirection:"column",gap:12,maxWidth:isDesktop?700:"100%",margin:isDesktop?"0 auto":0}}>
      {/* ── Hero: calories remaining ring + macro bars ── */}
      <Card style={{padding:16}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}>
          <div style={{display:"flex",alignItems:"center",gap:8}}>
            <span style={{fontSize:12,fontWeight:800,color:V.text}}>Today</span>
            {nutritionStreak>0&&<span style={{fontSize:11,fontWeight:700,padding:"1px 6px",borderRadius:4,background:"rgba(251,191,36,0.12)",color:"#fbbf24"}}>🥗 {nutritionStreak}d streak</span>}
          </div>
          {/* Deficit/surplus vs TDEE if set, else vs goal */}
          {tCal>0&&(()=>{
            const baseline=parseInt(goals?.tdee)||calGoal;
            const lbl=parseInt(goals?.tdee)?"vs TDEE":"vs goal";
            const diff=tCal-baseline;
            return <span style={{fontSize:11,fontWeight:700,padding:"2px 8px",borderRadius:6,
              background:diff>100?`${V.warn}12`:diff<-100?`${V.accent}12`:V.hoverBg,
              color:diff>100?V.warn:diff<-100?V.accent:V.text3}}>
              {diff>0?"+":""}{diff} {lbl}
            </span>;
          })()}
        </div>
        <div style={{display:"flex",alignItems:"center",gap:18}}>
          {/* Remaining ring */}
          {(()=>{
            const remaining=calGoal-tCal;
            const pct=calGoal>0?Math.min(1,tCal/calGoal):0;
            const over=remaining<0;
            const r=46,c2=2*Math.PI*r;
            return(
              <div style={{position:"relative",width:112,height:112,flexShrink:0}}>
                <svg width={112} height={112} style={{transform:"rotate(-90deg)"}}>
                  <circle cx={56} cy={56} r={r} fill="none" stroke={V.divider} strokeWidth={9}/>
                  <circle cx={56} cy={56} r={r} fill="none" stroke={over?V.danger:pct>=0.85?V.accent:V.warn} strokeWidth={9}
                    strokeDasharray={c2} strokeDashoffset={c2*(1-pct)} strokeLinecap="round"
                    style={{transition:"stroke-dashoffset .6s ease, stroke .3s"}}/>
                </svg>
                <div style={{position:"absolute",inset:0,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center"}}>
                  <span style={{fontSize:22,fontWeight:900,fontFamily:V.mono,lineHeight:1,
                    color:over?V.danger:V.text}}>{Math.abs(remaining).toLocaleString()}</span>
                  <span style={{fontSize:11,color:V.text3,marginTop:2,textTransform:"uppercase",letterSpacing:".06em"}}>
                    {over?"cal over":"cal left"}</span>
                  <span style={{fontSize:11,color:V.text3,fontFamily:V.mono,marginTop:1}}>{tCal}/{calGoal}</span>
                </div>
              </div>
            );
          })()}
          {/* Macro remaining bars */}
          <div style={{flex:1,display:"flex",flexDirection:"column",gap:9}}>
            {[
              {l:"Protein",v:tProt,g:protGoal,c:"#f43f5e"},
              {l:"Carbs",v:tCarbs,g:goals?.carbs||250,c:V.warn},
              {l:"Fat",v:tFat,g:goals?.fat||70,c:V.accent2},
            ].map(m=>{
              const left=m.g-m.v;
              return(
                <div key={m.l}>
                  <div style={{display:"flex",justifyContent:"space-between",marginBottom:3}}>
                    <span style={{fontSize:11,fontWeight:700,color:V.text2}}>{m.l}</span>
                    <span style={{fontSize:11,fontFamily:V.mono,color:left<0?V.danger:V.text3}}>
                      {m.v}/{m.g}g{left>0?` · ${left}g left`:left<0?` · ${-left}g over`:" ✓"}
                    </span>
                  </div>
                  <div style={{height:5,borderRadius:3,background:V.hoverBg,overflow:"hidden"}}>
                    <div style={{height:"100%",borderRadius:3,width:`${m.g>0?Math.min(100,m.v/m.g*100):0}%`,
                      background:`linear-gradient(90deg,${m.c}cc,${m.c})`,transition:"width .5s"}}/>
                  </div>
                </div>
              );
            })}
            {/* Fiber bonus row when logged */}
            {((todayN?.fiber||0)>0||goals.fiber>0)&&(
              <div style={{fontSize:11,color:V.text3,fontFamily:V.mono}}>🌾 {todayN?.fiber||0}{goals.fiber>0?`/${goals.fiber}`:""}g fiber{todayN?.sugar>0?` · 🍬 ${todayN.sugar}g sugar`:""}{todayN?.sodium>0?` · 🧂 ${todayN.sodium}mg sodium`:""}</div>
            )}
          </div>
        </div>
        <CoachTargetsNote nt={nt}/>
      </Card>

      {/* 7-day macro adherence mini heatmap */}
      {s.nutrition.length>0&&(
        <Card style={{padding:12}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
            <div style={{fontSize:11,fontWeight:700,color:V.text}}>7-Day Adherence</div>
            <div style={{fontSize:11,color:V.text3}}>Cal & Protein goals</div>
          </div>
          <div style={{display:"flex",gap:4}}>
            {Array.from({length:7}).map((_,i)=>{
              const day=ago(6-i);
              const n=s.nutrition.find(x=>x.date===day);
              const calOk=n&&goals?.cal?n.cal>=goals.cal*0.85:false;
              const protOk=n&&goals?.protein?n.protein>=goals.protein*0.9:false;
              const both=calOk&&protOk;const either=calOk||protOk;
              const isToday=day===today();
              return(
                <div key={i} style={{flex:1,display:"flex",flexDirection:"column",gap:2,alignItems:"center"}}>
                  <div style={{width:"100%",height:28,borderRadius:5,
                    background:both?"linear-gradient(180deg,#22c55e,#16a34a)":either?"linear-gradient(180deg,#f59e0b,#d97706)":n?"rgba(244,63,94,0.25)":V.inputBg,
                    border:isToday?`1px solid ${V.accent}40`:"none",
                    boxShadow:both?"0 0 6px rgba(34,197,94,0.25)":"none"}}/>
                  <div style={{fontSize:11,color:isToday?V.accent:V.text3,fontWeight:isToday?700:400}}>
                    {["Su","Mo","Tu","We","Th","Fr","Sa"][new Date(day+"T12:00:00").getDay()]}
                  </div>
                </div>
              );
            })}
          </div>
          <div style={{display:"flex",gap:10,marginTop:6}}>
            {[{c:"#22c55e",l:"Both goals"},{c:"#f59e0b",l:"Partial"},{c:"rgba(244,63,94,0.4)",l:"Logged, missed"}].map(x=>(
              <div key={x.l} style={{display:"flex",alignItems:"center",gap:3}}>
                <div style={{width:8,height:8,borderRadius:2,background:x.c}}/>
                <span style={{fontSize:11,color:V.text3}}>{x.l}</span>
              </div>
            ))}
          </div>
        </Card>
      )}
      {/* #1: Water tracking — persistent tap counter */}
      <Card style={{padding:10}}>
        <div style={{display:"flex",flexDirection:"column",gap:8}}>
          <div style={{display:"flex",alignItems:"center",justifyContent:"space-between"}}>
            <div style={{display:"flex",alignItems:"center",gap:8}}>
              {Icons.droplet({size:16,color:V.accent2})}
              <span style={{fontSize:12,fontWeight:700,color:V.text}}>Water</span>
            </div>
            <div style={{display:"flex",alignItems:"center",gap:6}}>
              <button onClick={()=>{
                if(waterCount>0)setWater(waterCount-1);
              }} aria-label="Remove a cup of water" style={{width:32,height:32,borderRadius:8,background:V.inputBg,border:`1px solid ${V.cardBorder}`,
                cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",fontSize:16,color:V.text3}}>−</button>
              <div style={{minWidth:54,textAlign:"center"}}>
                <span style={{fontSize:20,fontWeight:800,color:V.accent2,fontFamily:V.mono}}>{waterCount}</span>
                <span style={{fontSize:11,color:V.text3}}>/{waterGoal}</span>
              </div>
              <button onClick={()=>{
                Haptic.light();
                const n=waterCount+1;setWater(n);
                if(n>=waterGoal)SuccessToastCtrl.show("💧 Hydration goal hit!");
              }} style={{width:32,height:32,borderRadius:8,background:`${V.accent2}15`,border:`1px solid ${V.accent2}25`,
                cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",fontSize:16,color:V.accent2,fontWeight:700}}>+</button>
            </div>
          </div>
          <Progress val={waterCount} max={waterGoal} color={V.accent2} h={6}/>
        </div>
      </Card>

      <div style={{display:"flex",gap:8}}>
        <Btn full onClick={start}>{Icons.plus({size:16,color:onAccent()})} Log Nutrition</Btn>
        {s.nutrition.find(n=>n.date===ago(1))&&(
          <Btn v="secondary" onClick={copyYesterday} s={{flexShrink:0,fontSize:11}}>{Icons.copy({size:14,color:V.text2})} Copy Yesterday</Btn>
        )}
      </div>
      <div style={{display:"flex",gap:8}}>
        <Btn v="secondary" full onClick={()=>{setIdeaMealTarget(null);setShowIdeas(true);}}>💡 Meal Ideas</Btn>
        <Btn v="secondary" full onClick={()=>setQuickAdd(true)}>⚡ Quick Add</Btn>
      </div>

      {/* Meal Favorites */}
      {(()=>{
        const favs=LS.get("ft-meal-favs")||[];
        if(favs.length===0)return null;
        return(
          <div>
            <div style={{fontSize:11,fontWeight:700,color:V.text3,marginBottom:6}}>SAVED MEALS</div>
            <div style={{display:"flex",gap:6,overflowX:"auto",paddingBottom:4,WebkitOverflowScrolling:"touch"}}>
              {favs.map((fav,i)=>(
                <button key={i} onClick={()=>{
                  // Load into today's entry (edit mode if one exists) — ADD_N would otherwise
                  // replace the existing log for that day, and a stale form date could hit another day
                  const ex=s.nutrition.find(n=>n.date===today());
                  setEditingNId(ex?ex.id:null);
                  setForm({...buildForm(ex),meals:fav.meals,cal:fav.cal?.toString()||"",protein:fav.protein?.toString()||"",
                    carbs:fav.carbs?.toString()||"",fat:fav.fat?.toString()||""});
                  setShow(true);SuccessToastCtrl.show(`Loaded: ${fav.name}`);
                }} style={{padding:"8px 12px",borderRadius:8,background:`${V.accent}06`,border:`1px solid ${V.accent}15`,
                  cursor:"pointer",whiteSpace:"nowrap",flexShrink:0,textAlign:"left"}}>
                  <div style={{fontSize:11,fontWeight:600,color:V.text}}>{fav.name}</div>
                  <div style={{fontSize:11,color:V.text3,fontFamily:V.mono}}>{fav.cal}cal · {fav.protein}P</div>
                </button>
              ))}
            </div>
          </div>
        );
      })()}

      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
        <Stat icon={Icons.flame} label="Avg Calories" value={avgCal} unit="kcal" color={V.warn}/>
        <Stat icon={Icons.target} label="Avg Protein" value={avgP} unit="g" color={V.accent}
          sub={week.length?`${protHitDays}/${Math.min(week.length,7)}d hit goal`:undefined}/>
      </div>
      {/* Protein hit rate bar */}
      {week.length>0&&(
        <div style={{padding:"8px 12px",borderRadius:10,background:V.card,border:`1px solid ${V.cardBorder}`}}>
          <div style={{display:"flex",justifyContent:"space-between",marginBottom:5}}>
            <span style={{fontSize:11,color:V.text3,fontWeight:700}}>PROTEIN GOAL RATE (7d)</span>
            <span style={{fontSize:11,fontWeight:800,color:protHitRate>=80?"#22c55e":protHitRate>=50?V.warn:"#f43f5e",fontFamily:V.mono}}>{protHitRate}%</span>
          </div>
          <div style={{height:6,borderRadius:3,background:V.hoverBg}}>
            <div style={{height:"100%",borderRadius:3,transition:"width .5s",
              width:`${protHitRate}%`,
              background:protHitRate>=80?"linear-gradient(90deg,#22c55e,#86efac)":protHitRate>=50?"linear-gradient(90deg,#f59e0b,#fcd34d)":"linear-gradient(90deg,#f43f5e,#fb7185)"}}/> 
          </div>
        </div>
      )}

      {/* ── T3 #17: Nutrition gap/pattern analysis ── */}
      {(()=>{
        if(s.nutrition.length<7)return null;
        const protGoal=goals?.protein||150;
        const calGoal=goals?.cal||2200;
        const dayNames=["Sun","Mon","Tue","Wed","Thu","Fri","Sat"];
        // Analyse last 28 days grouped by day-of-week
        const dowData=Array.from({length:7},(_,dow)=>{
          const entries=s.nutrition.filter(n=>{
            const d=new Date(n.date+"T12:00:00");
            return d.getDay()===dow&&n.date>=ago(27);
          });
          return{
            dow,name:dayNames[dow],
            count:entries.length,
            protHit:entries.filter(n=>n.protein>=protGoal*0.9).length,
            calHit:entries.filter(n=>n.cal>=calGoal*0.8&&n.cal<=calGoal*1.15).length,
            avgProt:entries.length?Math.round(entries.reduce((a,n)=>a+(n.protein||0),0)/entries.length):0,
          };
        });
        // Find worst protein days (logged but consistently missed)
        const weakDays=dowData.filter(d=>d.count>=2&&d.protHit/d.count<0.5).map(d=>d.name);
        // Find strongest days
        const strongDays=dowData.filter(d=>d.count>=2&&d.protHit/d.count>=0.85).map(d=>d.name);
        // Streak insights: how many days logged this week vs last week
        const thisW=s.nutrition.filter(n=>n.date>=ago(6)).length;
        const lastW=s.nutrition.filter(n=>n.date>=ago(13)&&n.date<ago(6)).length;
        const insights=[];
        if(weakDays.length>0)insights.push({icon:"⚠️",color:V.warn,text:`Protein gap on ${weakDays.slice(0,2).join(" & ")} — plan ahead`});
        if(strongDays.length>0)insights.push({icon:"💪",color:V.accent,text:`Nailing protein on ${strongDays.slice(0,2).join(" & ")} — keep that up`});
        if(thisW>lastW)insights.push({icon:"📈",color:V.accent,text:`${thisW} days logged this week vs ${lastW} last — consistency building`});
        else if(thisW<lastW&&lastW>=4)insights.push({icon:"📉",color:V.warn,text:`${lastW-thisW} fewer logs than last week — stay on track`});
        // Check if frequently under-eating
        const under=s.nutrition.filter(n=>n.date>=ago(13)&&n.cal>0&&n.cal<calGoal*0.75).length;
        if(under>=4)insights.push({icon:"🔋",color:V.danger,text:`Under-fuelling ${under} days lately — calories too low`});
        if(insights.length===0)return null;
        return(
          <div style={{padding:"12px 14px",borderRadius:12,
            background:V.card,border:`1px solid ${V.cardBorder}`}}>
            <div style={{fontSize:11,fontWeight:700,color:V.text3,textTransform:"uppercase",
              letterSpacing:".06em",marginBottom:9}}>📊 Patterns</div>
            {/* 7-day DOW protein heat strip */}
            <div style={{display:"flex",gap:3,marginBottom:10}}>
              {dowData.map(d=>{
                const rate=d.count>0?d.protHit/d.count:null;
                const bg=rate===null?V.inputBg:rate>=0.85?"rgba(0,245,160,0.35)":rate>=0.5?"rgba(245,158,11,0.35)":"rgba(244,63,94,0.25)";
                return(
                  <div key={d.dow} style={{flex:1,display:"flex",flexDirection:"column",alignItems:"center",gap:2}}>
                    <div style={{width:"100%",height:20,borderRadius:4,background:bg,
                      display:"flex",alignItems:"center",justifyContent:"center"}}>
                      {d.count>0&&<span style={{fontSize:11,fontWeight:700,color:"rgba(255,255,255,0.5)"}}>{d.avgProt}g</span>}
                    </div>
                    <span style={{fontSize:11,color:V.text3}}>{d.name.slice(0,2)}</span>
                  </div>
                );
              })}
            </div>
            <div style={{display:"flex",flexDirection:"column",gap:5}}>
              {insights.slice(0,3).map((ins,i)=>(
                <div key={i} style={{display:"flex",alignItems:"flex-start",gap:6}}>
                  <span style={{fontSize:12,flexShrink:0}}>{ins.icon}</span>
                  <span style={{fontSize:11,color:V.text2,lineHeight:1.4}}>{ins.text}</span>
                </div>
              ))}
            </div>
          </div>
        );
      })()}

      <Card>
        <span style={{fontSize:11,color:V.text3,fontWeight:700,display:"block",marginBottom:12,textTransform:"uppercase",letterSpacing:".06em"}}>Macros</span>
        <ResponsiveContainer width="100%" height={150}>
          <BarChart data={macroChart} barSize={8}>
            <CartesianGrid {...chartCfg.grid} vertical={false}/>
            <XAxis dataKey="date" {...chartCfg.axis} interval="preserveStartEnd"/>
            <YAxis {...chartCfg.axis} width={28}/>
            <Tooltip {...chartCfg.tip}/>
            <Bar dataKey="P" stackId="a" fill={V.accent}/>
            <Bar dataKey="C" stackId="a" fill={V.accent2}/>
            <Bar dataKey="F" stackId="a" fill={V.warn} radius={[3,3,0,0]}/>
          </BarChart>
        </ResponsiveContainer>
      </Card>

      {show&&(
        <Sheet title={editingNId?"Edit Nutrition":"Log Nutrition"} onClose={requestCloseLog}
          footer={<Btn full onClick={save}>{Icons.check({size:16,color:onAccent()})} {editingNId?"Update":"Save"} Nutrition</Btn>}>
          <Field label="Date" type="date" value={form.date} onChange={v=>setForm(f=>({...f,date:v}))}/>

          {/* Meals with food database search */}
          {form.meals.map((meal,mi)=>{
            const mCal=(meal.items||[]).reduce((s,i)=>s+i.cal,0);
            const mP=(meal.items||[]).reduce((s,i)=>s+i.protein,0);
            return(
              <div key={mi} style={{marginBottom:12,padding:12,background:V.card,borderRadius:14,border:`1px solid ${V.cardBorder}`}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
                  <div style={{display:"flex",alignItems:"center",gap:6}}>
                    <span style={{fontSize:13,fontWeight:700,color:V.text}}>{meal.name}</span>
                    <input type="time" value={meal.time||""} onChange={e=>setForm(f=>{const ms=[...f.meals];ms[mi]={...ms[mi],time:e.target.value};return{...f,meals:ms};})}
                      style={{padding:"6px 8px",borderRadius:8,background:V.inputBg,border:`1px solid ${V.cardBorder}`,
                        color:V.text3,fontSize:11,fontFamily:V.mono,outline:"none",width:80,minHeight:32}}/>
                  </div>
                  <div style={{display:"flex",gap:6,alignItems:"center"}}>
                    {mCal>0&&<span style={{fontSize:11,color:V.warn,fontFamily:V.mono}}>{mCal}cal · {mP}P</span>}
                    <button onClick={()=>{setIdeaMealTarget(mi);setIdeaType("All");setIdeaTag("All");setShowIdeas(true);}}
                      aria-label={`Meal ideas for ${meal.name}`}
                      style={{background:`${V.warn}12`,border:"none",borderRadius:8,padding:"4px 8px",cursor:"pointer",
                        fontSize:11,WebkitTapHighlightColor:"transparent"}}>💡</button>
                    <button onClick={()=>setScanMeal(mi)}
                      aria-label={`Scan barcode for ${meal.name}`}
                      style={{background:`${V.accent2}15`,border:"none",borderRadius:8,padding:"4px 8px",cursor:"pointer",
                        display:"flex",alignItems:"center",gap:3,WebkitTapHighlightColor:"transparent"}}>
                      {Icons.scan({size:12,color:V.accent2})}
                    </button>
                    <button onClick={()=>setActiveMeal(activeMeal===mi?null:mi)}
                      style={{background:`${V.accent}15`,border:"none",borderRadius:8,padding:"4px 10px",cursor:"pointer",
                        fontSize:11,color:V.accent,fontWeight:600,WebkitTapHighlightColor:"transparent"}}>
                      {activeMeal===mi?"Close":"+ Food"}
                    </button>
                  </div>
                </div>
                {(meal.items||[]).map((item,fi)=>(
                  <div key={fi} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"5px 0",borderBottom:`1px solid ${V.divider}`}}>
                    <span style={{fontSize:12,color:V.text2,flex:1,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{item.name}</span>
                    <div style={{display:"flex",alignItems:"center",gap:6,flexShrink:0}}>
                      <span style={{fontSize:11,color:V.text3,fontFamily:V.mono}}>{item.cal} · {item.protein}P</span>
                      <button onClick={()=>rmFoodFromMeal(mi,fi)} aria-label="Remove food item" style={{background:"none",border:"none",padding:2,cursor:"pointer"}}>{Icons.x({size:12,color:V.text3})}</button>
                    </div>
                  </div>
                ))}
                {activeMeal===mi&&<div style={{marginTop:8}}><FoodSearch onAdd={(food)=>addFoodToMeal(mi,food)}/></div>}
              </div>
            );
          })}

          {totalItems>0&&<Btn v="small" full onClick={autoCalc} s={{marginBottom:12}}>Recalculate totals from food items</Btn>}
          {totalItems>0&&!showFavInput&&(
            <button onClick={()=>setShowFavInput(true)} style={{width:"100%",padding:"8px",borderRadius:8,background:V.card,
              border:`1px dashed ${V.cardBorder}`,cursor:"pointer",fontSize:11,color:V.accent,
              fontFamily:V.font,marginBottom:12}}>⭐ Save as Meal Favorite</button>
          )}
          {showFavInput&&(
            <div style={{display:"flex",gap:6,marginBottom:12}}>
              <input value={favName} onChange={e=>setFavName(e.target.value)} placeholder="Name (e.g. My usual day)"
                autoFocus style={{flex:1,padding:"8px 10px",background:V.card,border:`1px solid ${V.cardBorder}`,
                  borderRadius:8,color:V.text,fontSize:12,outline:"none",fontFamily:V.font}}/>
              <Btn onClick={()=>{
                if(!favName.trim()){setShowFavInput(false);return;}
                autoCalc();
                const t=setTimeout(()=>{
                  const favs=LS.get("ft-meal-favs")||[];
                  const cal=form.meals.reduce((s2,m)=>(m.items||[]).reduce((a,i)=>a+i.cal,0)+s2,0);
                  const protein=form.meals.reduce((s2,m)=>(m.items||[]).reduce((a,i)=>a+i.protein,0)+s2,0);
                  const carbs=form.meals.reduce((s2,m)=>(m.items||[]).reduce((a,i)=>a+(i.carbs||0),0)+s2,0);
                  const fat=form.meals.reduce((s2,m)=>(m.items||[]).reduce((a,i)=>a+(i.fat||0),0)+s2,0);
                  const updated=[{name:favName.trim(),meals:form.meals,cal,protein,carbs,fat},...favs.filter(f=>f.name!==favName.trim())].slice(0,10);
                  LS.set("ft-meal-favs",updated);
                  SuccessToastCtrl.show(`Saved: ${favName.trim()}`);
                  setFavName("");setShowFavInput(false);
                },100);
                _nutTimersRef.current.push(t);
              }} s={{flexShrink:0}}>Save</Btn>
              <button onClick={()=>{setFavName("");setShowFavInput(false);}} style={{padding:"8px",borderRadius:8,
                background:V.inputBg,border:`1px solid ${V.cardBorder}`,cursor:"pointer",fontSize:11,color:V.text3}}>✕</button>
            </div>
          )}

          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
            <Field label="Calories" type="number" value={form.cal} onChange={v=>setForm(f=>({...f,cal:v}))} unit="kcal"/>
            <Field label="Protein" type="number" value={form.protein} onChange={v=>setForm(f=>({...f,protein:v}))} unit="g"/>
            <Field label="Carbs" type="number" value={form.carbs} onChange={v=>setForm(f=>({...f,carbs:v}))} unit="g"/>
            <Field label="Fat" type="number" value={form.fat} onChange={v=>setForm(f=>({...f,fat:v}))} unit="g"/>
          </div>
          <div style={{fontSize:11,fontWeight:700,color:V.text3,textTransform:"uppercase",letterSpacing:".06em",marginBottom:8}}>Micros & Recovery <span style={{fontWeight:400,textTransform:"none"}}>(optional)</span></div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
            <Field label="Fiber" type="number" value={form.fiber} onChange={v=>setForm(f=>({...f,fiber:v}))} unit="g"/>
            <Field label="Sugar" type="number" value={form.sugar} onChange={v=>setForm(f=>({...f,sugar:v}))} unit="g"/>
            <Field label="Sodium" type="number" value={form.sodium} onChange={v=>setForm(f=>({...f,sodium:v}))} unit="mg"/>
            <Field label="Water" type="number" value={form.water} onChange={v=>setForm(f=>({...f,water:v}))} unit="cups"/>
            <Field label="Sleep" type="number" value={form.sleep} onChange={v=>setForm(f=>({...f,sleep:v}))} unit="hrs" step="0.5"/>
          </div>
          {/* Was never rendered before — save() would set warnings and silently stall */}
          {valWarnings&&<ValidationWarning warnings={valWarnings} onConfirm={()=>{setValWarnings(null);save();}} onCancel={()=>setValWarnings(null)}/>}
        </Sheet>
      )}

      {/* ── Meal Ideas browser ── */}
      {showIdeas&&(
        <Sheet title={ideaMealTarget!==null?`Ideas → ${form.meals[ideaMealTarget]?.name||"Meal"}`:"Meal Ideas"}
          onClose={()=>{setShowIdeas(false);setIdeaMealTarget(null);}}>
          <div style={{display:"flex",gap:5,flexWrap:"wrap",marginBottom:8}}>
            {MEAL_TYPES.map(t=><Chip key={t} label={t} active={ideaType===t} onClick={()=>setIdeaType(t)}/>)}
          </div>
          <div style={{display:"flex",gap:4,overflowX:"auto",paddingBottom:8,marginBottom:8,WebkitOverflowScrolling:"touch"}}>
            {MEAL_TAGS.map(t=>(
              <button key={t} onClick={()=>setIdeaTag(t)} style={{padding:"4px 10px",borderRadius:14,fontSize:11,
                fontWeight:600,fontFamily:V.font,cursor:"pointer",whiteSpace:"nowrap",flexShrink:0,WebkitTapHighlightColor:"transparent",
                border:`1px solid ${ideaTag===t?`${V.warn}45`:"transparent"}`,
                background:ideaTag===t?`${V.warn}14`:V.hoverBg,color:ideaTag===t?V.warn:V.text3}}>{t}</button>
            ))}
          </div>
          {MEAL_IDEAS.filter(m=>(ideaType==="All"||m.type===ideaType)&&(ideaTag==="All"||m.tags.includes(ideaTag))).map((idea,i)=>(
            <Card key={i} onClick={()=>addIdea(idea)} style={{marginBottom:10,padding:14}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:2}}>
                <div style={{fontSize:14,fontWeight:700,color:V.text}}>{idea.n}</div>
                <span style={{fontSize:11,fontWeight:700,padding:"2px 7px",borderRadius:5,flexShrink:0,
                  background:`${V.accent2}12`,color:V.accent2}}>{idea.type}</span>
              </div>
              <div style={{fontSize:11,color:V.text3,marginBottom:8}}>{idea.desc}</div>
              <div style={{display:"flex",gap:10,fontSize:11,fontFamily:V.mono,marginBottom:8}}>
                <span style={{color:V.warn,fontWeight:800}}>{idea.cal} cal</span>
                <span style={{color:V.accent}}>{idea.p}P</span>
                <span style={{color:V.accent2}}>{idea.c}C</span>
                <span style={{color:V.warn}}>{idea.f}F</span>
              </div>
              <div style={{fontSize:11,color:V.text2,lineHeight:1.5}}>
                {idea.items.map(it=>it.name.split("(")[0].trim()).join(" · ")}
              </div>
              {idea.tags.length>0&&(
                <div style={{display:"flex",gap:4,marginTop:7,flexWrap:"wrap"}}>
                  {idea.tags.map(t=>(
                    <span key={t} style={{fontSize:11,fontWeight:700,padding:"1px 6px",borderRadius:4,
                      background:`${V.purple}10`,color:V.purple}}>{t}</span>
                  ))}
                </div>
              )}
              <div style={{fontSize:11,color:V.accent,marginTop:8,fontWeight:700,fontFamily:V.mono}}>tap to add all {idea.items.length} items →</div>
            </Card>
          ))}
        </Sheet>
      )}

      {/* ── Quick Add modal ── */}
      {quickAdd&&(
        <div style={{position:"fixed",top:0,left:0,right:0,bottom:0,zIndex:2000,display:"flex",alignItems:"center",justifyContent:"center",padding:20}}>
          <div style={{position:"absolute",top:0,left:0,right:0,bottom:0,background:"rgba(0,0,0,.7)",backdropFilter:"blur(4px)"}} onClick={()=>setQuickAdd(false)}/>
          <div role="dialog" aria-modal="true" aria-label="Quick add" style={{position:"relative",background:V.sheetBg,
            borderRadius:18,padding:22,maxWidth:320,width:"100%",border:`1px solid ${V.cardBorder}`,
            boxShadow:V.shadowLg,animation:"popIn .22s cubic-bezier(.2,.8,.3,1) both"}}>
            <SectionTitle icon={Icons.zap} color={V.warn} size={15} style={{marginBottom:4}}>Quick add</SectionTitle>
            <div style={{fontSize:11,color:V.text3,lineHeight:1.5,marginBottom:14}}>Bump today's totals without itemizing — great for estimates.</div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
              <Field label="Calories" type="number" value={quick.cal} onChange={v=>setQuick(q=>({...q,cal:v}))} unit="kcal" autoFocus/>
              <Field label="Protein" type="number" value={quick.protein} onChange={v=>setQuick(q=>({...q,protein:v}))} unit="g"/>
            </div>
            {/* Common quick portions */}
            <div style={{display:"flex",gap:5,flexWrap:"wrap",marginBottom:14}}>
              {[100,200,300,500].map(c=>(
                <button key={c} onClick={()=>setQuick(q=>({...q,cal:((parseInt(q.cal)||0)+c).toString()}))}
                  style={{padding:"5px 10px",borderRadius:8,background:`${V.warn}10`,border:`1px solid ${V.warn}22`,
                    cursor:"pointer",fontSize:11,fontWeight:700,color:V.warn,fontFamily:V.mono}}>+{c}</button>
              ))}
            </div>
            <div style={{display:"flex",gap:10}}>
              <Btn v="secondary" full onClick={()=>setQuickAdd(false)}>Cancel</Btn>
              <Btn full onClick={saveQuickAdd} disabled={!(parseInt(quick.cal)||parseInt(quick.protein))}>Add</Btn>
            </div>
          </div>
        </div>
      )}

      {scanMeal!==null&&(
        <BarcodeScanner
          onResult={(food)=>addFoodToMeal(scanMeal,food)}
          onClose={()=>setScanMeal(null)}
        />
      )}

      {/* History — tap to expand, pencil to edit */}
      {s.nutrition.length>0&&<div style={{fontSize:12,fontWeight:700,color:V.text3,marginBottom:-4}}>HISTORY</div>}
      {s.nutrition.slice(0,14).map(n=>{
        const expanded=expandedN===n.id;
        const itemCount=(n.meals||[]).reduce((a,m)=>a+(m.items||[]).length,0);
        return(
        <Card key={n.id} onClick={()=>setExpandedN(expanded?null:n.id)} style={{padding:14,cursor:"pointer"}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
            <div style={{display:"flex",alignItems:"center",gap:6}}>
              <span style={{fontSize:12,color:V.text2,fontWeight:600}}>{fmtFull(n.date)}</span>
              {n.sleep>0&&<span style={{fontSize:11,color:V.purple}}>{Icons.moon({size:10,color:V.purple})} {(parseFloat(n.sleep)||0).toFixed(1)}h</span>}
              {itemCount>0&&<span style={{fontSize:11,color:V.text3}}>{itemCount} items</span>}
            </div>
            <div style={{display:"flex",gap:2,alignItems:"center"}}>
              <button onClick={e=>{e.stopPropagation();editEntry(n);}} aria-label="Edit nutrition entry"
                style={{background:"none",border:"none",padding:4,cursor:"pointer",fontSize:11,color:V.text3}}>✏️</button>
              <button onClick={e=>{e.stopPropagation();ConfirmCtrl.show("Delete nutrition log?",fmtFull(n.date),()=>{Undo.set("Nutrition deleted",n,"nutrition");d({type:"DEL_N",id:n.id});SuccessToastCtrl.show("Nutrition deleted");});}} aria-label="Delete nutrition entry" style={{background:"none",border:"none",padding:4,cursor:"pointer"}}>{Icons.trash({size:13,color:V.text3})}</button>
            </div>
          </div>
          <div style={{display:"flex",gap:12,marginTop:8,alignItems:"baseline"}}>
            <span style={{fontSize:18,fontWeight:700,color:V.warn,fontFamily:V.mono}}>{n.cal}<span style={{fontSize:11,color:V.text3}}> kcal</span></span>
            <span style={{fontSize:12,color:V.accent,fontFamily:V.mono}}>{n.protein}P</span>
            <span style={{fontSize:12,color:V.accent2,fontFamily:V.mono}}>{n.carbs}C</span>
            <span style={{fontSize:12,color:V.warn,fontFamily:V.mono}}>{n.fat}F</span>
            {n.fiber>0&&<span style={{fontSize:11,color:V.text3,fontFamily:V.mono}}>{n.fiber}Fb</span>}
            {n.water>0&&<span style={{fontSize:11,color:V.accent2}}>{Icons.droplet({size:9,color:V.accent2})} {n.water}</span>}
          </div>
          {/* Macro split bar — protein/carbs/fat calorie share */}
          {(()=>{
            const pc=(n.protein||0)*4,cc=(n.carbs||0)*4,fc=(n.fat||0)*9;
            const tot=pc+cc+fc;if(tot<=0)return null;
            return(
              <div style={{display:"flex",height:5,borderRadius:3,overflow:"hidden",marginTop:8,background:V.hoverBg}}>
                <div style={{width:`${pc/tot*100}%`,background:V.accent}}/>
                <div style={{width:`${cc/tot*100}%`,background:V.accent2}}/>
                <div style={{width:`${fc/tot*100}%`,background:V.warn}}/>
              </div>
            );
          })()}
          {/* Meal timing */}
          {n.meals?.some(m=>m.time)&&(
            <div style={{display:"flex",gap:8,marginTop:6,flexWrap:"wrap"}}>
              {n.meals.filter(m=>m.time).map((m,i)=>(
                <span key={i} style={{fontSize:11,padding:"2px 6px",borderRadius:4,background:V.card,color:V.text3}}>
                  {m.name} {m.time}
                </span>
              ))}
            </div>
          )}
          {/* Expanded: full meal breakdown + micros */}
          {expanded&&(
            <div style={{marginTop:10,paddingTop:10,borderTop:`1px solid ${V.divider}`}}>
              {(n.meals||[]).filter(m=>(m.items||[]).length>0).map((m,mi2)=>(
                <div key={mi2} style={{marginBottom:8}}>
                  <div style={{display:"flex",justifyContent:"space-between",marginBottom:3}}>
                    <span style={{fontSize:11,fontWeight:700,color:V.accent2}}>{m.name}{m.time?` · ${m.time}`:""}</span>
                    <span style={{fontSize:11,color:V.text3,fontFamily:V.mono}}>
                      {(m.items||[]).reduce((a,i)=>a+(i.cal||0),0)} cal · {(m.items||[]).reduce((a,i)=>a+(i.protein||0),0)}P
                    </span>
                  </div>
                  {(m.items||[]).map((it,ii)=>(
                    <div key={ii} style={{display:"flex",justifyContent:"space-between",padding:"2px 0 2px 10px"}}>
                      <span style={{fontSize:11,color:V.text2,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",paddingRight:8}}>{it.name}</span>
                      <span style={{fontSize:11,color:V.text3,fontFamily:V.mono,flexShrink:0}}>{it.cal} · {it.protein}P</span>
                    </div>
                  ))}
                </div>
              ))}
              {itemCount===0&&<div style={{fontSize:11,color:V.text3,fontStyle:"italic"}}>Totals only — no itemized foods</div>}
              {(n.sugar>0||n.sodium>0)&&(
                <div style={{fontSize:11,color:V.text3,fontFamily:V.mono,marginTop:4}}>
                  {n.sugar>0?`🍬 ${n.sugar}g sugar`:""}{n.sugar>0&&n.sodium>0?" · ":""}{n.sodium>0?`🧂 ${n.sodium}mg sodium`:""}
                </div>
              )}
            </div>
          )}
          <Progress val={n.cal} max={goals.cal} color={V.warn} h={4}/>
        </Card>
        );
      })}
    </div>
  );
}

// ═══════════════════════════════════════
//  BODY TAB
// ═══════════════════════════════════════
// ─── Height string to meters ───
